import { IDocumentRequest } from '../../../models/utilities/document-request';
import { ShipmentStatus } from '../enums/shipment-status';
import { ShipmentType } from '../enums/shipment-type';
import { DocumentRouteBillOfLading } from './document-route-bill-of-lading';
import { DocumentRouteCommercialInvoices } from './document-route-commercial-invoices';
import { DocumentRouteCommercialInvoiceExport } from './document-route-commercial-invoices-export';
import { DocumentRouteDeliverySchedule } from './document-route-delivery-schedule';
import { DocumentRouteLoadRequisition } from './document-route-load-requisistion';
import { DocumentRouteLoaderReport } from './document-route-loader-report';
import { DocumentRouteLoadsheet } from './document-route-loadsheet';
import { DocumentRoutePaperwork } from './document-route-paperwork';
import { DocumentRouteRunlog } from './document-route-runlog';
import { DocumentRouteRunlogExcel } from './document-route-runlog-excel';
import { DocumentRouteScanReport } from './document-route-scan-report';

/**
 * A document that is got for a shipment, as those under the documents of its details are.
 */
export interface ShipmentDocumentType {
    key         : string;
    display     : string;
    icon        : string;
    /** The kinds of shipment that have the document */
    types       : ShipmentType[];
    create      : (id : number, type : ShipmentType) => IDocumentRequest;
    /** Why the document is not there for a shipment of the status, when it is not */
    unavailable : (status : ShipmentStatus) => string | undefined;
}

const always = () => undefined;

// Most of the documents are of a shipment that is going somewhere, and not of one that has come back
const untilClosed = (status : ShipmentStatus) =>
    status >= ShipmentStatus.Closed ? 'Not available once closed' : undefined;

// The scan report is of what was scanned as the shipment was made
const fromProduction = (status : ShipmentStatus) =>
    status < ShipmentStatus.InProduction ? 'Not available before in production' : undefined;

const pdf = 'la-file-pdf-o';
const excel = 'la-file-excel-o';

const both = [ShipmentType.Truck, ShipmentType.Container];
const containersOnly = [ShipmentType.Container];

/**
 * The documents that the details of a shipment has, in its order.  What is on a shipment decides whether some of them
 * are made at all, as a shipment has to have a delivery for a load sheet, so those the API says it has none of.
 */
export const ShipmentDocumentTypes : ShipmentDocumentType[] = [
    {
        key: 'paperwork', display: 'Paperwork', icon: pdf, types: both, unavailable: always,
        create: (id, type) => new DocumentRoutePaperwork(id, type)
    },
    {
        key: 'runlog', display: 'Run Log', icon: pdf, types: both, unavailable: untilClosed,
        create: (id, type) => new DocumentRouteRunlog(id, type)
    },
    {
        key: 'runlog-excel', display: 'Run Log (XLSX)', icon: excel, types: both, unavailable: untilClosed,
        create: (id, type) => new DocumentRouteRunlogExcel(id, type)
    },
    {
        key: 'bill-of-lading', display: 'Bill of Ladings', icon: pdf, types: containersOnly, unavailable: untilClosed,
        create: (id, type) => new DocumentRouteBillOfLading(id, type)
    },
    {
        key: 'commercial-invoices', display: 'Commercial Invoices', icon: pdf, types: both, unavailable: untilClosed,
        create: (id, type) => new DocumentRouteCommercialInvoices(id, type)
    },
    {
        key: 'commercial-invoices-excel', display: 'Commercial Invoices (XLSX)', icon: excel, types: both, unavailable: untilClosed,
        create: (id, type) => new DocumentRouteCommercialInvoiceExport(id, type)
    },
    {
        key: 'loadsheet', display: 'Load Sheet', icon: pdf, types: both, unavailable: untilClosed,
        create: (id, type) => new DocumentRouteLoadsheet(id, type)
    },
    {
        key: 'delivery-schedules', display: 'Delivery Schedules', icon: pdf, types: both, unavailable: untilClosed,
        create: (id, type) => new DocumentRouteDeliverySchedule(id, type)
    },
    {
        key: 'load-requisition', display: 'Load Requisition', icon: pdf, types: containersOnly, unavailable: untilClosed,
        create: (id, type) => new DocumentRouteLoadRequisition(id, type)
    },
    {
        key: 'loader-report', display: 'Loader Report', icon: pdf, types: both, unavailable: untilClosed,
        create: (id, type) => new DocumentRouteLoaderReport(id, type)
    },
    {
        key: 'scan-report', display: 'Scan Report', icon: pdf, types: both, unavailable: fromProduction,
        create: (id, type) => new DocumentRouteScanReport(id, type)
    }
];

/**
 * The documents that there are for a kind of shipment
 * @param type The kind of shipment
 */
export const getShipmentDocumentTypes = (type : ShipmentType) : ShipmentDocumentType[] => {
    return ShipmentDocumentTypes.filter(x => x.types.indexOf(type) >= 0);
};
